import { useRef, useEffect, useMemo } from "react";
import { SECTION_COLORS } from "@/lib/music";

function seededBars(seed, n) {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) % 1000000007;
  const rand = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
  const bars = [];
  for (let i = 0; i < n; i++) {
    const env = 0.35 + 0.65 * Math.abs(Math.sin(i / (n / 6)));
    bars.push(0.15 + rand() * 0.85 * env);
  }
  return bars;
}

export default function Waveform({ seed = "song", currentTime, duration, onSeek, sections = [], loop = null }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const bars = useMemo(() => seededBars(seed, 160), [seed]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const progress = duration > 0 ? currentTime / duration : 0;
    const n = bars.length;
    const gap = 2;
    const bw = (W - gap * (n - 1)) / n;
    for (let i = 0; i < n; i++) {
      const x = i * (bw + gap);
      const h = bars[i] * H * 0.86;
      const y = (H - h) / 2;
      const played = i / n < progress;
      ctx.fillStyle = played ? "#06B6D4" : "#2A3342";
      ctx.beginPath();
      const r = Math.min(bw / 2, 2);
      ctx.roundRect(x, y, bw, h, r);
      ctx.fill();
    }
    // cursor
    if (progress > 0) {
      const cx = progress * W;
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fillRect(cx - 0.5, 0, 1.5, H);
    }
  }, [bars, currentTime, duration]);

  const handleClick = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    onSeek(ratio * duration);
  };

  return (
    <div className="space-y-1">
      <div
        ref={wrapRef}
        data-testid="waveform"
        onClick={handleClick}
        className="relative h-24 w-full cursor-pointer overflow-hidden rounded-xl bg-[#0B0E14] ring-1 ring-[#222A38]"
      >
        <canvas ref={canvasRef} className="h-full w-full" />
        {loop && duration > 0 && (
          <div
            className="pointer-events-none absolute inset-y-0 bg-cyan-400/15 ring-1 ring-inset ring-cyan-400/40"
            style={{ left: `${(loop.start / duration) * 100}%`, width: `${((loop.end - loop.start) / duration) * 100}%` }}
          />
        )}
      </div>
      {sections.length > 0 && duration > 0 && (
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-[#0B0E14]">
          {sections.map((s, i) => (
            <div
              key={`${s.name}-${s.start}`}
              title={s.name}
              className="absolute inset-y-0"
              style={{
                left: `${(s.start / duration) * 100}%`,
                width: `${((s.end - s.start) / duration) * 100}%`,
                backgroundColor: (SECTION_COLORS[s.name] || "#94A3B8") + "aa",
                borderRight: "1px solid #0B0E14",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
